import { useState, useEffect } from '@wordpress/element';
import apiFetch from '@wordpress/api-fetch';
import { addQueryArgs } from '@wordpress/url';

/**
 * Fetches a count from the WP REST API based on source config.
 * Returns the total count or null while loading.
 */
export default function useDynamicCount( attributes ) {
	const {
		source, postType, postStatus, taxonomy, termSlug,
		userRole, commentStatus, productFilter, productStock,
	} = attributes;
	const [ count, setCount ] = useState( null );

	useEffect( () => {
		if ( source === 'manual' || ! source ) {
			setCount( null );
			return;
		}

		let cancelled = false;

		async function fetchCount() {
			try {
				let total = 0;

				if ( source === 'posts' ) {
					const args = {
						per_page: 1,
						status: postStatus || 'publish',
						_fields: 'id',
					};
					if ( taxonomy && termSlug ) {
						args[ taxonomy ] = termSlug;
					}
					const response = await apiFetch( {
						path: addQueryArgs( `/wp/v2/${ getRestBase( postType ) }`, args ),
						parse: false,
					} );
					total = parseInt( response.headers.get( 'X-WP-Total' ), 10 ) || 0;
				} else if ( source === 'products' ) {
					const args = {
						per_page: 1,
						status: 'publish',
						_fields: 'id',
					};
					if ( productFilter === 'on_sale' ) {
						args.on_sale = true;
					} else if ( productFilter === 'featured' ) {
						args.featured = true;
					}
					if ( productStock ) {
						args.stock_status = productStock;
					}
					if ( termSlug ) {
						args.product_cat = termSlug;
					}
					const response = await apiFetch( {
						path: addQueryArgs( '/wc/store/v1/products', args ),
						parse: false,
					} );
					total = parseInt( response.headers.get( 'X-WP-Total' ), 10 ) || 0;
					// Fallback to wp/v2/product if WC Store API unavailable.
					if ( ! total && response.status !== 200 ) {
						const fallback = await apiFetch( {
							path: addQueryArgs( '/wp/v2/product', { per_page: 1, _fields: 'id' } ),
							parse: false,
						} );
						total = parseInt( fallback.headers.get( 'X-WP-Total' ), 10 ) || 0;
					}
				} else if ( source === 'users' ) {
					const args = { per_page: 1, _fields: 'id' };
					if ( userRole ) {
						args.roles = userRole;
					}
					const response = await apiFetch( {
						path: addQueryArgs( '/wp/v2/users', args ),
						parse: false,
					} );
					total = parseInt( response.headers.get( 'X-WP-Total' ), 10 ) || 0;
				} else if ( source === 'comments' ) {
					const args = { per_page: 1, _fields: 'id' };
					if ( commentStatus === 'approved' ) {
						args.status = 'approve';
					}
					const response = await apiFetch( {
						path: addQueryArgs( '/wp/v2/comments', args ),
						parse: false,
					} );
					total = parseInt( response.headers.get( 'X-WP-Total' ), 10 ) || 0;
				}

				if ( ! cancelled ) {
					setCount( total );
				}
			} catch {
				if ( ! cancelled ) {
					setCount( 0 );
				}
			}
		}

		fetchCount();

		return () => {
			cancelled = true;
		};
	}, [ source, postType, postStatus, taxonomy, termSlug, userRole, commentStatus, productFilter, productStock ] );

	return count;
}

/**
 * Map post type slug to REST base. Handles common types.
 */
function getRestBase( slug ) {
	const map = {
		post: 'posts',
		page: 'pages',
		attachment: 'media',
	};
	return map[ slug ] || slug;
}
